import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Button,
    Checkbox,
    createFilterOptions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    TextField
} from '@mui/material';

import { useAppState } from './AppStateContext';
import CopyButton from './CopyButton';

// Match dropdown options on name or email, so an existing student can be
// found either way.
const filterStudents = createFilterOptions({
    stringify: (option) => `${option.name || ''} ${option.email}`,
});

const NewStudentDialog = ({ open, onClose, classId, classStudents }) => {
    const { apiGet, apiPost } = useAppState();

    const [schoolStudents, setSchoolStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [newStudentName, setNewStudentName] = useState("");
    const [newStudentEmail, setNewStudentEmail] = useState("");
    const [noEmail, setNoEmail] = useState(false);
    const [credentials, setCredentials] = useState(null);
    const [isNoNameError, setIsNoNameError] = useState(false);
    const [isNoEmailError, setIsNoEmailError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        apiGet('students')
            // If the list cannot be loaded the dialog still works as a
            // plain name-and-email form, so a failure is not surfaced.
            .then(response => { if (!cancelled) setSchoolStudents(response.data); })
            .catch(() => {});
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const classEmails = useMemo(() =>
        new Set((classStudents || []).map(student => student.email.toLowerCase())),
        [classStudents]
    );

    const availableStudents = useMemo(() =>
        schoolStudents.filter(student => !classEmails.has(student.email.toLowerCase())),
        [schoolStudents, classEmails]
    );

    // If the teacher bypasses the dropdown and types the email of an
    // existing school student, treat it as adding that student rather
    // than letting the server create a lookalike or fail opaquely.
    const typedEmail = newStudentEmail.trim().toLowerCase();
    const emailMatch = (!noEmail && !selectedStudent && typedEmail) ?
        schoolStudents.find(student => student.email.toLowerCase() === typedEmail) ?? null
        : null;
    const emailMatchInClass = emailMatch !== null && classEmails.has(emailMatch.email.toLowerCase());

    const existingStudent = selectedStudent || (emailMatchInClass ? null : emailMatch);

    const handleCreate = async () => {
        // A double click must not create two students (with the no-email
        // path each click would mint a distinct generated account).
        if (isSubmitting || emailMatchInClass) return;

        if (!existingStudent) {
            if (!newStudentName) {
                setIsNoNameError(true);
            }

            if (!noEmail && !newStudentEmail) {
                setIsNoEmailError(true);
            }

            if (!newStudentName || (!noEmail && !newStudentEmail)) {
                return;
            }
        }

        setIsSubmitting(true);

        // For an existing student, resend the stored name: the server
        // updates the name from the request, and the teacher is adding
        // the student to the class, not renaming them.
        const studentData = existingStudent ? {
            name: existingStudent.name || existingStudent.email,
            email: existingStudent.email,
            class_uuid: classId,
        } : noEmail ? {
            name: newStudentName,
            no_email: true,
            class_uuid: classId,
        } : {
            name: newStudentName,
            email: newStudentEmail,
            class_uuid: classId,
        };

        try {
            const response = await apiPost('students', studentData);
            if (response.data.generated) {
                setCredentials({
                    email: response.data.student.email,
                    password: response.data.password,
                });
            } else {
                handleClose(true);
            }
        } catch (error) {
            if (error.response?.data?.errors) {
                // The errors value is either a list of strings or Laravel's
                // {field: [messages]} shape; flatten to cover both.
                const message = Object.values(error.response.data.errors).flat().join('; ');
                setErrorMessage(message);
            } else {
                setErrorMessage("An error occurred while trying to add the student.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStudentChange = (e, value) => {
        setSelectedStudent(value && typeof value === 'object' && !value.isNew ? value : null);
        setIsNoNameError(false);
        setIsNoEmailError(false);
        setErrorMessage(null);
    }

    const handleNameInputChange = (e, value, reason) => {
        setNewStudentName(value);
        // Editing the text again after picking a student reverts the
        // dialog to the new-student form ('reset' is the programmatic
        // fill-in of the picked student's own name, which must stick).
        if (reason === 'input') {
            setSelectedStudent(null);
        }
        setIsNoNameError(false);
        setErrorMessage(null);
    }

    const handleEmailChange = (e) => {
        setNewStudentEmail(e.target.value);
        setIsNoEmailError(false);
        setErrorMessage(null);
    }

    const handleNoEmailChange = (e) => {
        setNoEmail(e.target.checked);
        setIsNoEmailError(false);
        setErrorMessage(null);
    }

    const handleClose = (updated) => {
        setSelectedStudent(null);
        setNewStudentName('');
        setNewStudentEmail('');
        setNoEmail(false);
        setCredentials(null);
        setIsNoNameError(false);
        setIsNoEmailError(false);
        setErrorMessage(null);
        setIsSubmitting(false);
        onClose(updated);
    };

    const emailHelperText =
        isNoEmailError ? "Email Address is required" :
        emailMatchInClass ? "This student is already in this class." :
        emailMatch ? "A student with this email already exists at your school and will be added to this class." :
        "";

    return (
        // While the generated credentials are on screen, a backdrop click or
        // Escape must not dismiss them (they cannot be shown again) — only
        // the explicit Close button closes the dialog then. Likewise while a
        // submission is in flight: closing then would throw away the
        // credentials pane a no-email creation is about to show.
        <Dialog open={open} onClose={() => { if (!credentials && !isSubmitting) handleClose(false); }}>
            {credentials ? (
                <>
                    <DialogTitle>Student Added</DialogTitle>
                    <DialogContent>
                        <p>
                            An account has been created for {newStudentName}. Write these
                            credentials down now and share them with the student and/or
                            their parents — the password cannot be shown again, only reset.
                        </p>
                        <p style={{ marginBottom: 0 }}>
                            Username: <span style={{ fontFamily: 'monospace' }}>{credentials.email}</span>
                            {' '}<CopyButton text={credentials.email} label="Copy username" />
                        </p>
                        <p style={{ marginTop: 4 }}>
                            Password: <span style={{ fontFamily: 'monospace' }}>{credentials.password}</span>
                            {' '}<CopyButton text={credentials.password} label="Copy password" />
                        </p>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => handleClose(true)} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogTitle>Add Student</DialogTitle>
                    <DialogContent>
                        <Autocomplete
                            freeSolo
                            options={availableStudents}
                            value={selectedStudent}
                            onChange={handleStudentChange}
                            onInputChange={handleNameInputChange}
                            isOptionEqualToValue={(option, value) => option.email === value.email}
                            getOptionLabel={(option) =>
                                typeof option === 'string' ? option :
                                option.isNew ? option.inputValue :
                                option.name || option.email
                            }
                            filterOptions={(options, params) => {
                                const filtered = filterStudents(options, params);
                                if (params.inputValue) {
                                    filtered.push({ isNew: true, inputValue: params.inputValue });
                                }
                                return filtered;
                            }}
                            renderOption={(props, option) => (
                                <li {...props} key={option.isNew ? '' : option.email}>
                                    {option.isNew ? (
                                        `Add “${option.inputValue}” as a new student`
                                    ) : (
                                        <span>
                                            {option.name || option.email}
                                            {option.name &&
                                                <span style={{ color: 'gray', marginLeft: 8 }}>{option.email}</span>
                                            }
                                        </span>
                                    )}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    autoFocus
                                    required
                                    margin="dense"
                                    label="Student Name"
                                    fullWidth
                                    error={isNoNameError}
                                    helperText={isNoNameError ? "Student Name is required" : ""}
                                />
                            )}
                        />
                        {selectedStudent ? (
                            <p>
                                {selectedStudent.name || selectedStudent.email} will be added
                                to this class with their existing
                                account{selectedStudent.name ? ` (${selectedStudent.email})` : ''}.
                            </p>
                        ) : (
                            <>
                                {!noEmail &&
                                    <TextField
                                        required
                                        margin="dense"
                                        label="Email Address"
                                        type="email"
                                        fullWidth
                                        value={newStudentEmail}
                                        onChange={handleEmailChange}
                                        error={isNoEmailError || emailMatchInClass}
                                        helperText={emailHelperText}
                                    />
                                }
                                <FormControlLabel
                                    control={<Checkbox checked={noEmail} onChange={handleNoEmailChange} />}
                                    label="Student has no email address"
                                />
                                {noEmail &&
                                    <p style={{ marginTop: 0 }}>
                                        A username and password will be generated for the student.
                                    </p>
                                }
                            </>
                        )}
                        {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => handleClose(false)} color="primary" disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreate} color="primary" disabled={isSubmitting || emailMatchInClass}>
                            {existingStudent ? 'Add to Class' : 'Add'}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default NewStudentDialog;
